import { collectionMethods } from './collectionMethods.js';

const BASE_PATH = '/dealership-operations';

/**
 * Dealership Operations — a dealer family's brand, and the Companies standing
 * behind it.
 *
 * The Auction Operation's shape without the knockouts: an Operation bars people
 * from the houses it runs, and a dealer group runs none.
 *
 * Every route below writes its own path out in full. Several of them appear on
 * other nouns word for word, and that repetition is deliberate: a reader asking
 * what `setLegalCompany` reaches should find the answer here rather than in a
 * factory that assembles it from a base path passed in from somewhere else.
 */
export function dealershipOperationMethods({ sendRequest, buildUrl }) {
    return {
        ...collectionMethods({ basePath: BASE_PATH, sendRequest, buildUrl }),

        /**
         * The Company this Operation legally is.
         *
         * @param {string|null} companyId - `null` clears it.
         */
        async setLegalCompany(id, companyId) {
            return await sendRequest(
                buildUrl(`${BASE_PATH}/${encodeURIComponent(id)}/legal-company`),
                'PUT',
                { companyId }
            );
        },

        /**
         * The outside Company managing this Operation.
         *
         * Manager and owner are two Companies on one document.
         *
         * @param {string|null} companyId - `null` clears it.
         */
        async setManagementCompany(id, companyId) {
            return await sendRequest(
                buildUrl(`${BASE_PATH}/${encodeURIComponent(id)}/management-company`),
                'PUT',
                { companyId }
            );
        },

        /**
         * Who pays, and under which Stripe subscription.
         *
         * The server takes one of two bodies here and nothing between them:
         * `{ companyId, stripeSubscriptionId }` to name a payer, or
         * `{ companyId: null }` alone to clear. So a clear sends one key rather
         * than two, and a caller that passes a subscription with a null payer
         * gets the refusal it earned rather than a silently dropped field.
         *
         * @param {string|null} companyId
         * @param {string} [stripeSubscriptionId] - Required whenever a payer is named.
         */
        async setBillingCompany(id, companyId, stripeSubscriptionId) {
            const body = { companyId };
            if (stripeSubscriptionId !== undefined) {
                body.stripeSubscriptionId = stripeSubscriptionId;
            }
            return await sendRequest(
                buildUrl(`${BASE_PATH}/${encodeURIComponent(id)}/billing-company`),
                'PUT',
                body
            );
        },

        /**
         * Set one policy. The shape is the policy's own — the server stores it
         * as given and the application validates it against the key.
         */
        async setPolicy(id, policyName, policy) {
            return await sendRequest(
                buildUrl(`${BASE_PATH}/${encodeURIComponent(id)}/policies/${encodeURIComponent(policyName)}`),
                'PUT',
                policy
            );
        },

        /** Remove one policy. */
        async clearPolicy(id, policyName) {
            return await sendRequest(
                buildUrl(`${BASE_PATH}/${encodeURIComponent(id)}/policies/${encodeURIComponent(policyName)}`),
                'DELETE'
            );
        },

        /**
         * A page of the Dealerships this Operation runs.
         *
         * Answers the same `{ meta, documents }` page the list and search routes
         * answer, and pages the same way.
         */
        async listDealerships(id, { page, pageSize } = {}) {
            return await sendRequest(
                buildUrl(`${BASE_PATH}/${encodeURIComponent(id)}/dealerships`, { page, pageSize }),
                'GET'
            );
        }
    };
}

export default dealershipOperationMethods;
